import React, { useState, useEffect, useCallback } from 'react';
import { HMSPrebuilt } from '@100mslive/roomkit-react';
import { useSearchParams } from 'react-router-dom';

const baseUrl = process.env.REACT_APP_BASE_URL;

const CoacheePage = () => {
  const [searchParams] = useSearchParams();
  const [meetingStatus, setMeetingStatus] = useState('waiting'); // waiting, not_ready, active, error
  const [roomCode, setRoomCode] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isPolling, setIsPolling] = useState(true);

  // Get parameters from URL
  const lang = searchParams.get('lang') || 'en';
  const meetingRef = searchParams.get('meetingRef');
  const urlRoomCode = searchParams.get('roomCode');

  // Join meeting function
  const joinMeeting = useCallback(async () => {
    if (!meetingRef) return;

    try {
      const response = await fetch(`${baseUrl}/api/user/join_meeting`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          meetingRef: meetingRef,
          userType: 'coachee',
          lang: lang
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (urlRoomCode) {
        setRoomCode(urlRoomCode);
        setMeetingStatus('active');
        setIsPolling(false);
      } else {
        throw new Error('No room code received from backend');
      }
    } catch (error) {
      console.error('Error joining meeting:', error);
      setErrorMessage('Failed to join meeting. Please try again later.');
      setMeetingStatus('error');
    }
  }, [meetingRef, lang, urlRoomCode]);

  // Check meeting status from backend
  const checkMeetingStatus = useCallback(async () => {
    if (!meetingRef || !isPolling) return;

    try {
      const response = await fetch(`${baseUrl}/api/hms/session/check_meeting_status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ meetingRef: meetingRef }) // Adjust body as per backend requirements
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if ((data.meetingstatus === 'started') && urlRoomCode) {
        joinMeeting();
      } else if ((data.meetingstatus === 'waiting')) {
        setErrorMessage('Meeting Not Started Yet. Please wait for your coach to start the meeting.');
        setMeetingStatus('waiting');
        setIsPolling(true);
      } else if (data.meetingstatus === 'not_ready') {
        setMeetingStatus('not_ready');
        setIsPolling(true);
      } else {
        setErrorMessage('Meeting is not available');
        setMeetingStatus('error');
        setIsPolling(false);
      }
    } catch (error) {
      console.error('Error checking meeting status:', error);
      setErrorMessage('Failed to check meeting status. Please try again later.');
      setMeetingStatus('error');
      setIsPolling(false);
    }
  }, [meetingRef, isPolling, joinMeeting, urlRoomCode]);


  // Poll meeting status every 5 seconds
  useEffect(() => {
    if (!meetingRef) {
      setErrorMessage('Missing meeting reference. Please check your meeting link.');
      setMeetingStatus('error');
      return;
    }

    // Initial check
    checkMeetingStatus();

    // Set up polling
    const interval = setInterval(() => {
      if (isPolling && meetingStatus === 'waiting') {
        checkMeetingStatus();
      }
    }, 5000); // Poll every 5 seconds

    return () => clearInterval(interval);
  }, [meetingRef, checkMeetingStatus, isPolling, meetingStatus]);


  const handleLeave = useCallback(() => {
      console.log('user left the room');
      // Redirect to specific URL after leaving the session
      const redirectUrl = `${baseUrl}/${lang}/user/session/${meetingRef}`;
      window.location.href = redirectUrl;
    }, [lang, meetingRef]);

  // Render waiting state
  const renderWaitingState = () => (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      backgroundColor: '#f5f5f5',
      fontFamily: 'Arial, sans-serif',
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '40px',
        borderRadius: '10px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        textAlign: 'center',
        maxWidth: '500px',
        width: '100%'
      }}>
        <div style={{
          width: '60px',
          height: '60px',
          border: '4px solid #e0e0e0',
          borderTop: '4px solid #2196F3',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 30px'
        }}></div>
        
        <h2 style={{
          color: '#333',
          marginBottom: '20px',
          fontSize: '24px'
        }}>
          Waiting for Coach
        </h2>
        
        <p style={{
          color: '#666',
          lineHeight: '1.6',
          marginBottom: '30px'
        }}>
          Your coach will start the session shortly. Please wait while we prepare your meeting room.
        </p>
        
        <div style={{
          backgroundColor: '#f9f9f9',
          padding: '15px',
          borderRadius: '5px',
          marginBottom: '20px'
        }}>
          <small style={{ color: '#888' }}>
            Meeting Reference: {meetingRef}
          </small>
        </div>
        
        <button
          onClick={joinMeeting}
          style={{
            backgroundColor: '#2196F3',
            color: 'white',
            border: 'none',
            padding: '12px 30px',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '16px',
            marginRight: '10px'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#1976D2'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#2196F3'}
        >
          Try to Join Now
        </button>
        
        <button
          onClick={() => window.location.href = '/'}
          style={{
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            padding: '12px 30px',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '16px'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#5a6268'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#6c757d'}
        >
          Go Back
        </button>
      </div>
      
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );

  // Render not ready state
  const renderNotReadyState = () => (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      backgroundColor: '#f5f5f5',
      fontFamily: 'Arial, sans-serif',
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '40px',
        borderRadius: '10px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        textAlign: 'center',
        maxWidth: '500px',
        width: '100%',
        border: '2px solid #f39c12'
      }}>
        <div style={{
          fontSize: '48px',
          color: '#f39c12',
          marginBottom: '20px'
        }}>
          ⏰
        </div>
        
        <h2 style={{
          color: '#f39c12',
          marginBottom: '20px',
          fontSize: '24px'
        }}>
          Session Not Ready
        </h2>
        
        <p style={{
          color: '#666',
          lineHeight: '1.6',
          marginBottom: '20px'
        }}>
          The meeting time hasn't started yet. You can join the session up to 10 minutes before the scheduled time.
        </p>
        
        <div style={{
          backgroundColor: '#f9f9f9',
          padding: '15px',
          borderRadius: '5px',
          marginBottom: '30px'
        }}>
          <small style={{ color: '#888' }}>
            Meeting Reference: {meetingRef}
          </small>
        </div>

        <button
          onClick={checkMeetingStatus}
          style={{
            backgroundColor: '#f39c12',
            color: 'white',
            border: 'none',
            padding: '12px 30px',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '16px',
            marginRight: '10px'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#e67e22'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#f39c12'}
        >
          Check Again
        </button>
        
        <button
          onClick={() => window.location.href = '/'}
          style={{
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            padding: '12px 30px',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '16px'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#5a6268'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#6c757d'}
        >
          Go Back
        </button>
      </div>
    </div>
  );

  // Render error state
  const renderErrorState = () => (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      backgroundColor: '#f5f5f5',
      fontFamily: 'Arial, sans-serif',
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '40px',
        borderRadius: '10px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        textAlign: 'center',
        maxWidth: '500px',
        width: '100%',
        border: '2px solid #f44336'
      }}>
        <div style={{
          fontSize: '48px',
          color: '#f44336',
          marginBottom: '20px'
        }}>
          ⚠️
        </div>
        
        <h2 style={{
          color: '#f44336',
          marginBottom: '20px',
          fontSize: '24px'
        }}>
          Unable to Join Meeting
        </h2>
        
        <p style={{
          color: '#666',
          lineHeight: '1.6',
          marginBottom: '30px'
        }}>
          {errorMessage || 'There was an error joining your meeting. Please contact your coach for assistance.'}
        </p>
        
        <button
          onClick={() => {
            setMeetingStatus('waiting');
            setIsPolling(true);
            setErrorMessage('');
            checkMeetingStatus();
          }}
          style={{
            backgroundColor: '#2196F3',
            color: 'white',
            border: 'none',
            padding: '12px 30px',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '16px',
            marginRight: '10px'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#1976D2'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#2196F3'}
        >
          Try Again
        </button>
        
        <button
          onClick={() => window.location.href = '/'}
          style={{
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            padding: '12px 30px',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '16px'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#5a6268'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#6c757d'}
        >
          Return Home
        </button>
      </div>
    </div>
  );

  // Main render logic
  if (meetingStatus === 'waiting') {
    return renderWaitingState();
  }
  
  if (meetingStatus === 'not_ready') {
    return renderNotReadyState();
  }
  
  if (meetingStatus === 'error') {
    return renderErrorState();
  }
  
  if (meetingStatus === 'active' && roomCode) {
    return (
      <div style={{ width: '100%', height: '100vh' }}>
        <HMSPrebuilt 
          roomCode={roomCode}
          onLeave={handleLeave}
        />
      </div>
    );
  }

  // Fallback render
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      backgroundColor: '#f5f5f5'
    }}>
      <div>Loading...</div>
    </div>
  );
};

export default CoacheePage;
